/**
 * Retenção de INSS sobre NFs de serviço (E7b) — tomadores e NFs por competência,
 * com crédito do mês, acumulado e preview da compensação de GPS. Padrão Mantine (U5).
 *
 * 02/09/2026 — CADASTRO COMPLETO de tomadores (pedido do dono: "o mínimo é
 * razão social e CNPJ"): botão "Novo tomador" (modal com razão social, CNPJ
 * com máscara/DV conferido no client para erro rápido — a validação final é
 * do servidor — e alíquota, default 11%), ações por tomador (Editar = mesmo
 * modal preenchido → PATCH; Ativar/Desativar — não há DELETE porque tomador
 * tem NFs ligadas), badge âmbar "CNPJ pendente" (registros antigos/seed sem
 * CNPJ — o Editar resolve) e inativos atenuados com badge cinza. O Select de
 * NOVA NF mostra só tomadores ATIVOS (NF antiga de inativo segue no histórico).
 */
import { useCallback, useEffect, useState } from "react";
import { Alert, Badge, Button, Card, Group, Modal, Select, Table, Text, TextInput, ThemeIcon, Title } from "@mantine/core";
import { IconBuildingStore, IconPencil, IconPower } from "@tabler/icons-react";
import { api, ErroApi } from "../api";
import { fmtMoeda, fmtCompetencia } from "../format";
import { normalizarCnpj, formatarCnpj } from "../cnpj";
import { CompetenciaField, MoedaField, PercentField } from "../componentes/entradas-mantine";
import { notificarOk, notificarErroApi } from "../componentes/notificacoes";
import { Carregando, Vazio } from "../componentes/Estados";
import { ItemEntidade, TabelaDados, Th, TdNum } from "../componentes/listas";

interface Tomador {
  id: string;
  nome: string;
  cnpj: string | null;
  aliquotaPpm: number;
  ativo: boolean;
  _count: { notasFiscais: number };
}
interface Nota {
  numero: string;
  descricao: string | null;
  valorCentavos: number;
  retencaoCentavos: number;
  tomador: string;
}
interface Retencao {
  competencia: number;
  creditoMesCentavos: number;
  acumuladoCentavos: number;
  notas: Nota[];
  previewCompensacaoInterna: {
    retencoesCentavos: number;
    seguradoCompensadoCentavos: number;
    empresaRatCompensadoCentavos: number;
    empresaRatSaldoCentavos: number;
    terceirosSaldoCentavos: number;
    irrfSaldoCentavos: number;
    saldoDarfCentavos: number;
  } | null;
}

/** Extrai a mensagem legível do erro da API (o POST/PATCH devolvem { erro }). */
function mensagemDoErro(e: unknown): string {
  if (e instanceof ErroApi) {
    const corpo = e.corpo as { erro?: unknown } | null;
    if (corpo && typeof corpo.erro === "string") return corpo.erro;
  }
  return e instanceof Error ? e.message : "Erro inesperado.";
}

export function TomadoresPage() {
  const [tomadores, setTomadores] = useState<Tomador[] | null>(null);
  const [comp, setComp] = useState<number | null>(202606);
  const [retencao, setRetencao] = useState<Retencao | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [recarregar, setRecarregar] = useState(0);
  const [fTom, setFTom] = useState("");
  const [fNum, setFNum] = useState("");
  const [fValor, setFValor] = useState<number | null>(null);
  const [fDesc, setFDesc] = useState("");
  // modal de cadastro/edição (02/09/2026)
  const [modalAberto, setModalAberto] = useState(false);
  const [editando, setEditando] = useState<Tomador | null>(null);
  const [mNome, setMNome] = useState("");
  const [mCnpj, setMCnpj] = useState("");
  const [mCnpjInvalido, setMCnpjInvalido] = useState(false);
  const [mAliquota, setMAliquota] = useState<number | null>(110000);
  const [mErro, setMErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  const carregarTomadores = useCallback(async () => {
    const r = await api.get<{ tomadores: Tomador[] }>("/tomadores");
    setTomadores(r.tomadores);
    return r.tomadores;
  }, []);

  useEffect(() => {
    carregarTomadores()
      .then((lista) => {
        const ativos = lista.filter((t) => t.ativo);
        if (ativos.length > 0) setFTom(ativos[0]!.id);
      })
      .catch(setErro);
  }, [carregarTomadores]);

  useEffect(() => {
    if (comp === null) return;
    api
      .get<Retencao>(`/competencias/${comp}/retencao`)
      .then(setRetencao)
      .catch(setErro);
  }, [comp, recarregar]);

  function abrirNovo() {
    setEditando(null);
    setMNome("");
    setMCnpj("");
    setMCnpjInvalido(false);
    setMAliquota(110000);
    setMErro(null);
    setModalAberto(true);
  }

  function abrirEdicao(t: Tomador) {
    setEditando(t);
    setMNome(t.nome);
    setMCnpj(t.cnpj ? formatarCnpj(t.cnpj) : "");
    setMCnpjInvalido(false);
    setMAliquota(t.aliquotaPpm);
    setMErro(null);
    setModalAberto(true);
  }

  async function salvarTomador() {
    const nome = mNome.trim();
    if (!nome) {
      setMErro("Informe a razão social do tomador.");
      return;
    }
    const cnpj = normalizarCnpj(mCnpj);
    if (!cnpj) {
      setMErro("CNPJ inválido — confira os 14 dígitos (com ou sem máscara).");
      setMCnpjInvalido(true);
      return;
    }
    if (mAliquota === null || mAliquota < 0 || mAliquota > 1_000_000) {
      setMErro("Alíquota inválida — informe um percentual entre 0% e 100%.");
      return;
    }
    setSalvando(true);
    setMErro(null);
    try {
      if (editando) {
        await api.patch(`/tomadores/${editando.id}`, { nome, cnpj, aliquotaPpm: mAliquota });
        notificarOk("Tomador atualizado.");
      } else {
        await api.post("/tomadores", { nome, cnpj, aliquotaPpm: mAliquota });
        notificarOk("Tomador cadastrado.");
      }
      setModalAberto(false);
      await carregarTomadores();
    } catch (e) {
      // 400/409 ficam no modal ABERTO (o dono corrige sem redigitar)
      setMErro(mensagemDoErro(e));
    } finally {
      setSalvando(false);
    }
  }

  async function alternarAtivo(t: Tomador) {
    try {
      await api.patch(`/tomadores/${t.id}`, { ativo: !t.ativo });
      notificarOk(t.ativo ? `Tomador ${t.nome} desativado.` : `Tomador ${t.nome} ativado.`);
      const lista = await carregarTomadores();
      // o Select de NF nova só lista ativos — se o selecionado saiu, corrige
      if (!lista.some((x) => x.ativo && x.id === fTom)) {
        setFTom(lista.find((x) => x.ativo)?.id ?? "");
      }
    } catch (e) {
      notificarErroApi(e);
    }
  }

  async function lancarNf() {
    try {
      setErro(null);
      await api.post(`/competencias/${comp}/notas-fiscais`, {
        tomadorId: fTom,
        numero: fNum,
        valorCentavos: fValor,
        descricao: fDesc || undefined,
      });
      notificarOk("NF lançada — crédito de retenção recalculado.");
      setFNum("");
      setFValor(null);
      setFDesc("");
      setRecarregar((x) => x + 1);
    } catch (e) {
      setErro(e);
      notificarErroApi(e);
    }
  }

  const tomadoresAtivos = (tomadores ?? []).filter((t) => t.ativo);

  return (
    <>
      <Group justify="space-between" mb={4}>
        <Title order={2}>Retenção de INSS sobre NFs (11%)</Title>
        <CompetenciaField label="Competência" valor={comp} onChange={setComp} />
      </Group>
      <Text size="sm" c="dimmed" mb="md">
        Lance as NFs de serviço do mês — a retenção de 11% vira crédito que compensa a GPS (preview abaixo).
      </Text>
      {erro != null && (
        <Alert color="red" title="Falha ao carregar" mb="sm">
          {String(erro)}
        </Alert>
      )}

      <Card withBorder shadow="xs" mb="md">
        <Group justify="space-between" mb="sm">
          <Title order={5}>Tomadores</Title>
          <Button size="xs" onClick={abrirNovo}>
            Novo tomador
          </Button>
        </Group>
        {!tomadores ? (
          <Carregando linhas={2} />
        ) : tomadores.length === 0 ? (
          <Vazio texto="Nenhum tomador cadastrado — use “Novo tomador”." />
        ) : (
          tomadores.map((t) => (
            <ItemEntidade
              key={t.id}
              opaco={!t.ativo}
              icone={
                <ThemeIcon variant="light" size="lg" color={t.ativo ? undefined : "gray"}>
                  <IconBuildingStore size={18} />
                </ThemeIcon>
              }
              titulo={t.nome}
              linhaSecundaria={t.cnpj ? `CNPJ ${formatarCnpj(t.cnpj)}` : "CNPJ pendente de cadastro"}
              badges={
                <>
                  {!t.ativo && (
                    <Badge variant="light" color="gray">
                      inativo
                    </Badge>
                  )}
                  {!t.cnpj && (
                    <Badge variant="light" color="yellow">
                      CNPJ pendente
                    </Badge>
                  )}
                  <Badge variant="light" color="sillis">
                    {(t.aliquotaPpm / 10000).toLocaleString("pt-BR")}%
                  </Badge>
                  <Badge variant="outline" color="gray">
                    {t._count.notasFiscais} NF(s)
                  </Badge>
                </>
              }
              acoes={[
                { rotulo: "Editar", icone: <IconPencil size={14} />, onClick: () => abrirEdicao(t) },
                {
                  rotulo: t.ativo ? "Desativar" : "Ativar",
                  icone: <IconPower size={14} />,
                  cor: t.ativo ? "red" : undefined,
                  onClick: () => void alternarAtivo(t),
                },
              ]}
            />
          ))
        )}
      </Card>

      <Modal
        opened={modalAberto}
        onClose={() => setModalAberto(false)}
        title={editando ? `Editar tomador — ${editando.nome}` : "Novo tomador"}
        centered
      >
        {mErro && (
          <Alert color="red" mb="sm" role="alert">
            {mErro}
          </Alert>
        )}
        <TextInput
          label="Razão social"
          placeholder="Nome completo do tomador"
          value={mNome}
          onChange={(e) => setMNome(e.currentTarget.value)}
          required
          mb="sm"
        />
        <TextInput
          label="CNPJ"
          placeholder="00.000.000/0000-00"
          value={mCnpj}
          error={mCnpjInvalido ? "CNPJ inválido — confira os dígitos" : undefined}
          onChange={(e) => {
            setMCnpj(e.currentTarget.value);
            setMCnpjInvalido(false);
          }}
          onBlur={() => {
            // máscara no blur + conferência rápida do DV (a validação final é do servidor)
            const d = mCnpj.replace(/\D/g, "");
            if (d.length === 14) {
              setMCnpj(formatarCnpj(d));
              setMCnpjInvalido(normalizarCnpj(d) === null);
            } else if (mCnpj.trim()) {
              setMCnpjInvalido(true);
            }
          }}
          required
          mb="sm"
        />
        <PercentField label="Alíquota de retenção" valor={mAliquota} onChange={(v) => setMAliquota(v)} mb="md" />
        <Group justify="flex-end">
          <Button variant="default" onClick={() => setModalAberto(false)}>
            Cancelar
          </Button>
          <Button onClick={() => void salvarTomador()} loading={salvando}>
            Salvar
          </Button>
        </Group>
      </Modal>

      <Card withBorder shadow="xs" mb="md">
        <Group justify="space-between" mb="sm">
          <Title order={5}>NFs de {comp ? fmtCompetencia(comp) : "—"}</Title>
          {retencao && (
            <Group gap="xs">
              <Badge size="lg" variant="light">Crédito do mês: {fmtMoeda(retencao.creditoMesCentavos)}</Badge>
              <Badge size="lg" variant="outline">Acumulado: {fmtMoeda(retencao.acumuladoCentavos)}</Badge>
            </Group>
          )}
        </Group>
        <Group align="flex-end" wrap="wrap" mb="sm">
          <Select
            label="Tomador"
            data={tomadoresAtivos.map((t) => ({ value: t.id, label: t.nome }))}
            value={fTom}
            onChange={(v) => setFTom(v ?? "")}
            w={320}
          />
          <TextInput label="Nº NF" value={fNum} onChange={(e) => setFNum(e.currentTarget.value)} w={90} />
          <MoedaField label="Valor da NF" valor={fValor} onChange={setFValor} w={130} />
          <TextInput label="Descrição (opcional)" value={fDesc} onChange={(e) => setFDesc(e.currentTarget.value)} w={280} />
          <Button onClick={() => void lancarNf()} disabled={!fNum || fValor === null || comp === null || !fTom}>
            Lançar NF
          </Button>
        </Group>

        {!retencao ? (
          <Carregando linhas={3} />
        ) : retencao.notas.length === 0 ? (
          <Vazio texto={`Nenhuma NF em ${fmtCompetencia(retencao.competencia)}.`} />
        ) : (
          <TabelaDados>
            <Table.Thead>
              <Table.Tr>
                <Th>Nº NF</Th>
                <Th>Descrição</Th>
                <Th>Tomador</Th>
                <Th num>Valor NF</Th>
                <Th num>Retenção</Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {retencao.notas.map((n) => (
                <Table.Tr key={n.numero}>
                  <Table.Td>
                    <Text ff="monospace" size="sm">{n.numero}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" c="dimmed">{n.descricao ?? ""}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" c="dimmed">{n.tomador}</Text>
                  </Table.Td>
                  <TdNum>{fmtMoeda(n.valorCentavos)}</TdNum>
                  <TdNum>{fmtMoeda(n.retencaoCentavos)}</TdNum>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </TabelaDados>
        )}

        {retencao?.previewCompensacaoInterna && (
          <>
            <Title order={5} mb="sm">Preview da compensação de GPS (com os devidos internos)</Title>
            <TabelaDados maw={560}>
              <Table.Tbody>
                {[
                  ["Retenções (crédito aplicado)", retencao.previewCompensacaoInterna.retencoesCentavos],
                  ["Compensado no INSS segurados (primeiro)", retencao.previewCompensacaoInterna.seguradoCompensadoCentavos],
                  ["Compensado no INSS empresa+RAT (restante)", retencao.previewCompensacaoInterna.empresaRatCompensadoCentavos],
                  ["Saldo empresa+RAT", retencao.previewCompensacaoInterna.empresaRatSaldoCentavos],
                  ["Terceiros", retencao.previewCompensacaoInterna.terceirosSaldoCentavos],
                  ["IRRF", retencao.previewCompensacaoInterna.irrfSaldoCentavos],
                ].map(([rotulo, valor]) => (
                  <Table.Tr key={rotulo as string}>
                    <Table.Td>{rotulo}</Table.Td>
                    <TdNum>{fmtMoeda(valor as number)}</TdNum>
                  </Table.Tr>
                ))}
                <Table.Tr>
                  <Table.Td>
                    <strong>Saldo DARF estimado</strong>
                  </Table.Td>
                  <Table.Td style={{ textAlign: "right" }}>
                    <strong>{fmtMoeda(retencao.previewCompensacaoInterna.saldoDarfCentavos)}</strong>
                  </Table.Td>
                </Table.Tr>
              </Table.Tbody>
            </TabelaDados>
          </>
        )}
      </Card>
    </>
  );
}
